import { useCallback, useEffect, useRef, useState } from 'react';
import { generateBsaReport, downloadBsaReportFile } from '../../../../store/dashboard/bankStatement/apiHelper';
import { BsaReportGenerateData } from '../../../../store/dashboard/bankStatement/types';
import { BSA_REPORT_FILE_EXTENSION } from '../../../../store/dashboard/bankStatement/constants';
import { getMimeTypeFromFileName } from '../../../../common/DocumentViewer/constants';

/**
 * Orchestrates the Banking tab "View Analysis" button: a single blocking
 * request generates the report, then opens it in the shared in-app viewer
 * drawer (no auto-download — the drawer's own Download action reuses the
 * same presigned URL).
 *
 * `orgId` is the currently-viewed org. BankDataTable does NOT remount on org
 * navigation, so a report request that is in flight when the user switches orgs
 * must be discarded rather than opening the previous org's report in the new
 * org's context.
 */
export function useBsaReport(orgId?: string) {
    const [loading, setLoading] = useState(false);
    const [showFile, setShowFile] = useState(false);
    const [fileUrl, setFileUrl] = useState('');
    const [fileMimeType, setFileMimeType] = useState('');
    const [fileName, setFileName] = useState('');
    const cancelledRef = useRef(false);
    // Re-entrancy guard for `trigger`: a ref (unlike `loading` state) is updated
    // synchronously, so two clicks in the same frame can't both slip past the guard
    // before the first render commits.
    const inFlightRef = useRef(false);
    // The org a request was fired for, compared against the current org on success
    // so a stale in-flight result never opens after an org switch.
    const requestOrgIdRef = useRef<string | undefined>(undefined);
    const currentOrgIdRef = useRef<string | undefined>(orgId);

    useEffect(() => {
        return () => {
            cancelledRef.current = true;
        };
    }, []);

    // Org navigation doesn't remount BankDataTable — track the current org and
    // close any open viewer when it changes, so the previous org's report can't
    // linger on screen (and an in-flight request is discarded via the orgId
    // check in openReport).
    useEffect(() => {
        currentOrgIdRef.current = orgId;
        setShowFile(false);
    }, [orgId]);

    const finish = useCallback(() => {
        inFlightRef.current = false;
        if (cancelledRef.current) return;
        setLoading(false);
    }, []);

    const openReport = useCallback(
        (data: BsaReportGenerateData) => {
            // Discard the result if the component unmounted or the user switched
            // orgs while this (long, blocking) generate was in flight.
            if (cancelledRef.current || requestOrgIdRef.current !== currentOrgIdRef.current) {
                finish();
                return;
            }
            setFileUrl(data.presignedUrl);
            setFileMimeType(getMimeTypeFromFileName(data.fileName, BSA_REPORT_FILE_EXTENSION));
            setFileName(data.fileName);
            setShowFile(true);
            finish();
        },
        [finish],
    );

    const trigger = useCallback(
        (triggerOrgId?: string) => {
            if (!triggerOrgId || inFlightRef.current) return;
            cancelledRef.current = false;
            requestOrgIdRef.current = triggerOrgId;
            inFlightRef.current = true;
            setLoading(true);

            generateBsaReport(triggerOrgId, { onSuccess: openReport, onError: finish });
        },
        [finish, openReport],
    );

    const closeFile = useCallback(() => setShowFile(false), []);

    const onDownload = useCallback(() => {
        if (fileUrl && fileName) downloadBsaReportFile(fileUrl, fileName);
    }, [fileUrl, fileName]);

    return { trigger, loading, showFile, fileUrl, fileMimeType, fileName, closeFile, onDownload };
}
